import React, { useEffect, useState } from 'react';
import { Plus, Pencil, Trash, X, Check, Image } from '@phosphor-icons/react';
import { useForm } from 'react-hook-form';
import axios from 'axios';
import { API } from '../../App';

const Concepts = () => {
  const [concepts, setConcepts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingConcept, setEditingConcept] = useState(null);
  const [uploading, setUploading] = useState(false);

  const { register, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm();

  const watchedImageUrl = watch('image_url');

  useEffect(() => {
    fetchConcepts();
  }, []);

  const fetchConcepts = async () => {
    try {
      const response = await axios.get(`${API}/concepts`);
      setConcepts(response.data);
    } catch (error) {
      console.error('Failed to fetch concepts:', error);
    } finally {
      setLoading(false);
    }
  };

  const openModal = (concept = null) => {
    if (concept) {
      setEditingConcept(concept);
      Object.keys(concept).forEach(key => {
        if (key === 'features') {
          setValue(key, concept[key].join('\n'));
        } else {
          setValue(key, concept[key]);
        }
      });
    } else {
      setEditingConcept(null);
      reset({
        title: '',
        subtitle: '',
        description: '',
        image_url: '',
        features: '',
        is_featured: true,
        order: concepts.length + 1
      });
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingConcept(null);
    reset();
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/admin/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setValue('image_url', `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`);
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Échec du téléchargement de l\'image');
    } finally {
      setUploading(false);
    }
  };

  const onSubmit = async (data) => {
    const payload = {
      ...data,
      features: data.features ? data.features.split('\n').filter(f => f.trim()) : []
    };

    try {
      if (editingConcept) {
        await axios.put(`${API}/admin/concepts/${editingConcept.id}`, payload, {
          withCredentials: true
        });
      } else {
        await axios.post(`${API}/admin/concepts`, payload, {
          withCredentials: true
        });
      }
      fetchConcepts();
      closeModal();
    } catch (error) {
      console.error('Failed to save concept:', error);
      alert('Erreur lors de la sauvegarde');
    }
  };

  const deleteConcept = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce concept ?')) return;

    try {
      await axios.delete(`${API}/admin/concepts/${id}`, {
        withCredentials: true
      });
      fetchConcepts();
    } catch (error) {
      console.error('Failed to delete concept:', error);
      alert('Erreur lors de la suppression');
    }
  };

  return (
    <div data-testid="admin-concepts">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-3xl text-white mb-2">Concepts</h1>
          <p className="text-stone-400">Gérez vos concepts de bars</p>
        </div>
        <button
          onClick={() => openModal()}
          data-testid="add-concept-btn"
          className="btn-primary flex items-center gap-2"
        >
          <Plus size={18} weight="bold" />
          Ajouter
        </button>
      </div>

      {/* Concepts List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="spinner" />
        </div>
      ) : concepts.length === 0 ? (
        <div className="admin-card p-12 text-center">
          <p className="text-stone-400 mb-4">Aucun concept pour le moment</p>
          <button onClick={() => openModal()} className="btn-secondary">
            Créer votre premier concept
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {concepts.map((concept) => (
            <div key={concept.id} className="admin-card overflow-hidden group">
              <div className="aspect-[16/10] relative">
                <img
                  src={concept.image_url}
                  alt={concept.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-4">
                  <button
                    onClick={() => openModal(concept)}
                    data-testid={`edit-concept-${concept.id}`}
                    className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
                  >
                    <Pencil size={18} weight="light" />
                  </button>
                  <button
                    onClick={() => deleteConcept(concept.id)}
                    data-testid={`delete-concept-${concept.id}`}
                    className="w-10 h-10 rounded-full bg-red-500/20 hover:bg-red-500/40 flex items-center justify-center text-red-400 transition-colors"
                  >
                    <Trash size={18} weight="light" />
                  </button>
                </div>
              </div>
              <div className="p-4">
                <h3 className="font-display text-lg text-white">{concept.title}</h3>
                <p className="text-sm text-stone-400 mt-1">{concept.subtitle}</p>
                <div className="flex items-center gap-2 mt-3">
                  {concept.is_featured && (
                    <span className="text-xs bg-[#C5A059]/20 text-[#C5A059] px-2 py-1 rounded">
                      En vedette
                    </span>
                  )}
                  <span className="text-xs text-stone-500">Ordre: {concept.order}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/80" onClick={closeModal} />
          <div className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-[#0A0A0A] border border-white/10 rounded-lg">
            <div className="sticky top-0 bg-[#0A0A0A] border-b border-white/5 p-6 flex items-center justify-between">
              <h2 className="font-display text-xl text-white">
                {editingConcept ? 'Modifier le Concept' : 'Nouveau Concept'}
              </h2>
              <button onClick={closeModal} className="text-stone-400 hover:text-white">
                <X size={24} weight="light" />
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
              {/* Title */}
              <div>
                <label className="block text-sm text-stone-400 mb-2">Titre *</label>
                <input
                  type="text"
                  className={`admin-input ${errors.title ? 'border-red-500' : ''}`}
                  {...register('title', { required: true })}
                />
              </div>

              {/* Subtitle */}
              <div>
                <label className="block text-sm text-stone-400 mb-2">Sous-titre *</label>
                <input
                  type="text"
                  className={`admin-input ${errors.subtitle ? 'border-red-500' : ''}`}
                  {...register('subtitle', { required: true })}
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm text-stone-400 mb-2">Description *</label>
                <textarea
                  rows={4}
                  className={`admin-input resize-none ${errors.description ? 'border-red-500' : ''}`}
                  {...register('description', { required: true })}
                />
              </div>

              {/* Image */}
              <div>
                <label className="block text-sm text-stone-400 mb-2">Image</label>
                <div className="flex gap-4">
                  <input
                    type="text"
                    placeholder="URL de l'image"
                    className="admin-input flex-1"
                    {...register('image_url', { required: true })}
                  />
                  <label className="btn-secondary cursor-pointer flex items-center gap-2">
                    <Image size={18} weight="light" />
                    {uploading ? 'Envoi...' : 'Upload'}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleImageUpload}
                      disabled={uploading}
                    />
                  </label>
                </div>
                {watchedImageUrl && (
                  <div className="mt-4 aspect-video w-full max-w-xs overflow-hidden rounded bg-[#121212]">
                    <img
                      src={watchedImageUrl}
                      alt="Preview"
                      className="w-full h-full object-cover"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  </div>
                )}
              </div>

              {/* Features */}
              <div>
                <label className="block text-sm text-stone-400 mb-2">
                  Caractéristiques (une par ligne)
                </label>
                <textarea
                  rows={4}
                  className="admin-input resize-none"
                  placeholder="Natural Stone Finish&#10;Brass Accents&#10;LED Backlighting"
                  {...register('features')}
                />
              </div>

              {/* Options */}
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm text-stone-400 mb-2">Ordre d'affichage</label>
                  <input
                    type="number"
                    className="admin-input"
                    {...register('order', { valueAsNumber: true })}
                  />
                </div>
                <div className="flex items-center gap-3 pt-6">
                  <input
                    type="checkbox"
                    id="is_featured"
                    className="w-4 h-4 accent-[#C5A059]"
                    {...register('is_featured')}
                  />
                  <label htmlFor="is_featured" className="text-sm text-stone-300">
                    Afficher en vedette
                  </label>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-4 pt-4">
                <button type="button" onClick={closeModal} className="btn-secondary">
                  Annuler
                </button>
                <button type="submit" className="btn-primary flex items-center gap-2">
                  <Check size={18} weight="bold" />
                  {editingConcept ? 'Sauvegarder' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Concepts;
